import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePagination } from "@/shared/hooks";
import type { SortConfig } from "@/shared/utils";
import { useGetVolunteersQuery } from "../api";
import type { Volunteer } from "../types";
import { useVolunteersSort, toggleSort, type VolunteerSortKey } from "./useVolunteersSort";

export function useVolunteersListPage() {
  const { t, i18n } = useTranslation("VolunteersPage");
  const locale = i18n.language as "ru" | "kk" | "en";

  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState<SortConfig<VolunteerSortKey> | null>(null);

  const { data, isLoading, isError } = useGetVolunteersQuery({ page_size: 9999 });

  const allVolunteers = data?.results ?? [];

  const filteredVolunteers = useMemo(() => {
    if (!search.trim()) return allVolunteers;
    const term = search.toLowerCase().trim();
    return allVolunteers.filter(function matchSearch(v: Volunteer) {
      return (
        v.full_name.toLowerCase().includes(term) ||
        v.email.toLowerCase().includes(term) ||
        v.phone.toLowerCase().includes(term)
      );
    });
  }, [allVolunteers, search]);

  const sortedVolunteers = useVolunteersSort(filteredVolunteers, sortConfig, locale);

  const pagination = usePagination(sortedVolunteers.length, 25, [search, sortConfig]);
  const pageVolunteers = sortedVolunteers.slice(pagination.startIndex, pagination.endIndex);

  function handleSort(key: VolunteerSortKey) {
    setSortConfig((prev) => toggleSort(prev, key));
  }

  return {
    volunteers: pageVolunteers,
    totalCount: sortedVolunteers.length,
    search,
    setSearch,
    sortConfig,
    handleSort,
    pagination,
    isLoading,
    isError,
    locale,
    t,
  };
}
